import { API } from "server/class";
import { swapServer } from "server";
import {
  MainNetInfoFromServer,
  MainNetInfo,
  WrapTokenFromServer,
  WrapToken,
} from "./type";
import { camelCase } from "../../utils/commonUtils";
import joi from "joi";

export const getMainNetInfoAPI = new API<
  // REQUEST_TYPE
  any,
  // RESPONSE_TYPE
  MainNetInfoFromServer,
  // PARSED_RESPONSE_TYPE
  MainNetInfo
>({
  serverInstance: swapServer,
  url: "/v1/swaps/main-net",
  method: "get",
  requestValidator: joi.any(),
  responseParser: camelCase,
});

export const getWrapTokensAPI = new API<
  // REQUEST_TYPE
  {
    query: {
      wrapTokenIdList?: number[];
      microChainId?: number;
      contractAddress?: string;
      ownerMicroChainId?: number;
      ownerMicroChainCurrencyId?: number;
    };
  },
  // RESPONSE_TYPE
  WrapTokenFromServer[],
  // PARSED_RESPONSE_TYPE
  WrapToken[]
>({
  serverInstance: swapServer,
  url: "/v1/swaps/wrap-tokens",
  method: "get",
  requestValidator: joi.object({
    query: joi.object({
      wrapTokenIdList: joi.array().items(joi.number()),
      microChainId: joi.number(),
      contractAddress: joi.string(),
      ownerMicroChainId: joi.number(),
      ownerMicroChainCurrencyId: joi.number(),
    }),
  }),
  responseParser: camelCase,
});

export const getSwapPairAPI = new API<
  // REQUEST_TYPE
  {
    query: {
      wrapTokenId: number;
    };
  },
  // RESPONSE_TYPE
  number[]
>({
  serverInstance: swapServer,
  url: "/v1/swaps/swap-pairs",
  method: "get",
  requestValidator: joi.object({
    query: joi.object({
      wrapTokenId: joi.number().required(),
    }),
  }),
});

export const getSwapsAPI = new API<
  // REQUEST_TYPE
  {
    query: {
      lastId?: number;
      limit?: number;
      walletAddress?: string;
      toTokenId?: number;
    };
  },
  // RESPONSE_TYPE
  {
    swap_id: number;
    from_token_id: number;
    to_token_id: number;
    from_token_value: string;
    to_token_value: string;
    status: number;
    wrap_tx_hash: string;
    fee: string;
    created_at: string;
    updated_at: string;
    deleted_at: string;
  }[],
  // PARSED_RESPONSE_TYPE
  {
    swapId: number;
    fromTokenId: number;
    toTokenId: number;
    fromTokenValue: string;
    toTokenValue: string;
    status: number;
    wrapTxHash: string;
    fee: string;
    createdAt: string;
    updatedAt: string;
    deletedAt: string;
  }[]
>({
  serverInstance: swapServer,
  url: "/v1/swaps",
  method: "get",
  requestValidator: joi.object({
    query: joi.object({
      lastId: joi.number(),
      limit: joi.number(),
      walletAddress: joi.string(),
      toTokenId: joi.number(),
    }),
  }),
  responseParser: camelCase,
});

export const getAmountsInAPI = new API<
  // REQUEST_TYPE
  {
    query: {
      amountOut: string;
      inContractAddress: string;
      outContractAddress: string;
    };
  },
  // RESPONSE_TYPE
  {
    amounts: string;
    totalNetworkFee: string;
  },
  // PARSED_RESPONSE_TYPE
  {
    amounts: string;
    totalNetworkFee: string;
  }
>({
  serverInstance: swapServer,
  url: "/v1/swaps/input-amounts",
  method: "get",
  requestValidator: joi.object({
    query: joi.object({
      amountOut: joi.string().required(),
      inContractAddress: joi.string().required(),
      outContractAddress: joi.string().required(),
    }),
  }),
  responseParser: camelCase,
});

export const getAmountsOutAPI = new API<
  // REQUEST_TYPE
  {
    query: {
      amountIn: string;
      inContractAddress: string;
      outContractAddress: string;
    };
  },
  // RESPONSE_TYPE
  {
    amounts: string;
    totalNetworkFee: string;
  },
  // PARSED_RESPONSE_TYPE
  {
    amounts: string;
    totalNetworkFee: string;
  }
>({
  serverInstance: swapServer,
  url: "/v1/swaps/output-amounts",
  method: "get",
  requestValidator: joi.object({
    query: joi.object({
      amountIn: joi.string().required(),
      inContractAddress: joi.string().required(),
      outContractAddress: joi.string().required(),
    }),
  }),
  responseParser: camelCase,
});

export const getSwapLimitAPI = new API<
  // REQUEST_TYPE
  {
    query: {
      unit: string;
      fromTokenId: number;
      toTokenId: number;
    };
  },
  // RESPONSE_TYPE
  {
    max: number;
    min: number;
  }
>({
  serverInstance: swapServer,
  url: "/v1/swaps/limit",
  method: "get",
  requestValidator: joi.object({
    query: joi.object({
      unit: joi.string().required(),
      fromTokenId: joi.number().required(),
      toTokenId: joi.number().required(),
    }),
  }),
});

export const getGatewayAPI = new API<
  // REQUEST_TYPE
  {
    query: {
      microChainId: number;
    };
  },
  // RESPONSE_TYPE
  {
    gateway_id: number;
    micro_chains_id: number;
    contract_address: string;
  }[],
  // PARSED_RESPONSE_TYPE
  {
    gatewayId: number;
    microChainsId: number;
    contractAddress: string;
  }[]
>({
  serverInstance: swapServer,
  url: "/v1/swaps/gateways",
  method: "get",
  requestValidator: joi.object({
    query: joi.object({
      microChainId: joi.number().required(),
    }),
  }),
  responseParser: camelCase,
});

export const createSwapAPI = new API<
  // REQUEST_TYPE
  {
    data: {
      userId: number;
      oldWalletAddress: string;
      fromTokenId: number;
      toTokenId: number;
      fromTokenValue: string;
      toTokenValue: string;
      fee: string;
      rawTransaction: string;
      walletAddress: string;
    };
    query: {
      microChainId: number;
    };
  },
  // RESPONSE_TYPE
  {
    swap_id: number;
    from_token_id: number;
    to_token_id: number;
    from_token_value: string;
    to_token_value: string;
    status: number;
    wrp_tx_hash: string;
    fee: string;
  },
  // PARSED_RESPONSE_TYPE
  {
    swapId: number;
    fromTokenId: number;
    toTokenId: number;
    fromTokenValue: string;
    toTokenValue: string;
    status: number;
    wrpTxHash: string;
    fee: string;
  }
>({
  serverInstance: swapServer,
  url: "/v1/swaps",
  method: "post",
  requestValidator: joi.object({
    data: joi.object({
      userId: joi.number().required(),
      oldWalletAddress: joi.string().required(),
      fromTokenId: joi.number().required(),
      toTokenId: joi.number().required(),
      fromTokenValue: joi.string().required(),
      toTokenValue: joi.string().required(),
      fee: joi.string().required(),
      rawTransaction: joi.string().required(),
      walletAddress: joi.string().required(),
    }),
    query: joi.object({
      microChainId: joi.number().required(),
    }),
  }),
  responseParser: camelCase,
});
